<script setup lang="ts">
import { computed, onMounted, onUnmounted, toRef, ref, watch, type ComputedRef } from 'vue'
import type { Player, P2PMessage } from '@/types'

import { useUIStore } from '@/stores'
import { useGameConfigStore } from '@/stores'
import { usePlayerStore } from '@/stores'
import { usePeer } from './composables/usePeer'
import { useGame } from './composables/useGame'
import { useQRCode } from './composables/useQRCode'
import { useGestures } from './composables/useGestures'
import { useSignaling } from './composables/useSignaling'

import HomeScreen from './components/HomeScreen.vue'
import LobbyScreen from './components/LobbyScreen.vue'
import GameScreen from './components/GameScreen.vue'
import ResultsScreen from './components/ResultsScreen.vue'
import GuestWaitingScreen from './components/GuestWaitingScreen.vue'
import HostDisconnectedScreen from './components/screens/HostDisconnectedScreen.vue'
import PlayerDisconnectedScreen from './components/screens/PlayerDisconnectedScreen.vue'
import JoinModal from './components/JoinModal.vue'
import OptionsModal from './components/OptionsModal.vue'
import RoomSettingsModal from './components/RoomSettingsModal.vue'
import QRModal from './components/QRModal.vue'
import RouletteModal from './components/RouletteModal.vue'

// Stores
const uiStore = useUIStore()
const configStore = useGameConfigStore()
const playerStore = usePlayerStore()

// UI state
const creatingRoom = ref(false)
const showRouletteModal = ref(false)

// Título de página dinámico
const pageTitle = computed(() => {
  const currentScreen = uiStore.currentScreen
  const showJoinModal = uiStore.showJoinModal
  const playerName = playerStore.myPlayerName
  const isHost = playerStore.isHost

  // Pantalla inicial
  if (currentScreen === 'home') {
    return 'Palabras - Inicio'
  }

  // Modal de unirse a sala abierto
  if (showJoinModal) {
    return 'Palabras - Invitado'
  }

  // Pantalla lobby (estamos en sala)
  if (currentScreen === 'lobby') {
    if (isHost) {
      return `Palabras - Host - ${playerName}`
    } else {
      return `Palabras - Invitado - ${playerName}`
    }
  }

  // Otras pantallas (game, results, guest-waiting)
  if (currentScreen === 'game' || currentScreen === 'results' || currentScreen === 'guest-waiting') {
    if (isHost) {
      return `Palabras - Host - ${playerName}`
    } else {
      return `Palabras - Invitado - ${playerName}`
    }
  }

  // Fallback
  return 'Palabras'
})

// Actualizar el título de la página cuando cambie
watch(pageTitle, (newTitle) => {
  document.title = newTitle
}, { immediate: true })

// Peer
const {
  peerConnections,
  peerInitialized,
  generatePlayerId,
  generateRoomId,
  generatePlayerName,
  initializePeer,
  createOffer,
  createAnswer,
  createOfferForGuest,
  acceptGuestAnswer,
  send,
  sendTo,
  broadcast,
  closeGuestConnection
} = usePeer()

// Server Signaling (for internet mode)
const {
  createRoomWithServer,
  joinRoomViaServer,
  addSlotToServer,
  startPollingForAnswers,
  stopPolling,
  stopAllPolling
} = useSignaling()

// Polling state for server-mediated signaling
const currentRoomId = ref<string | null>(null)

// Seleccionar mejor cámara al montar
onMounted(async () => {
  await selectBestCamera()
})

// Track guest slot for multiplayer (simple sequential ID)
const guestSlotCounter = ref(0)

// Estado para gestionar slots de guests pendientes
interface GuestSlot {
  id: string
  slotId: string
  offerCode: string
  status: 'pending' | 'connected'
  createdAt: number
}

interface CreatingSlot {
  id: string
  status: 'creating'
}

const guestSlots = ref<GuestSlot[]>([])
const creatingSlots = ref<CreatingSlot[]>([])

// Helper function to resolve player ID to slot ID if needed
const resolvePlayerIdToSlot = (playerId: string): string => {
  // If it's already a guest_slot, return as-is
  if (playerId.startsWith('guest_slot_')) {
    return playerId
  }
  
  // If it's a real player ID, use the efficient reverse mapping
  const slotId = playerIdToSlot.value.get(playerId)
  if (slotId) {
    return slotId
  }
  
  // Fallback: try to find in the forward mapping (for backward compatibility)
  for (const [slot, realId] of guestSlotToPlayerId.value.entries()) {
    if (realId === playerId) {
      console.warn('[App] Found slot in forward mapping but not reverse - updating cache:', slot, '→', playerId)
      playerIdToSlot.value.set(playerId, slot)
      return slot
    }
  }
  
  // Default to the original ID (for host or legacy cases)
  console.warn('[App] Could not resolve player ID to slot:', playerId, '- using original ID')
  return playerId
}

// Wrapper send function that handles host (broadcast) and guest (send to host)
const sendWrapper = (message: P2PMessage): void => {
  if (playerStore.isHost) {
    // Host: broadcast to all guests
    broadcast(message)
  } else {
    // Guest: send only to host
    send(message)
  }
}

// myPlayerId como ref reactivo desde el store
const myPlayerId = toRef(playerStore, 'myPlayerId')

// Game - ahora usa stores internamente
const {
  gameStarted,
  currentRound,
  totalRounds,
  currentPlayerIndex,
  isMyTurn,
  timeLeft,
  currentCategory,
  currentWord,
  usedWords,
  rankings,
  currentPlayer,
  startGame: gameStartGame,
  correctAnswer: gameCorrectAnswer,
  skipWord: gameSkipWord,
  handleP2PMessage: gameHandleP2PMessage
} = useGame({
  send: sendWrapper,
  myPlayerId
})

// QR
const qrCode = useQRCode()
const { 
  QrcodeStream, 
  scannerActive, 
  generateQR, 
  onDetect, 
  onCameraReady, 
  onError,
  resetScannerState,
  getCameras,
  selectBestCamera,
  saveCameraId,
  availableCameras
} = qrCode

// SelectedCameraId reactivo para componentes
const selectedCameraId = computed(() => qrCode.selectedCameraId.value || '')

// Wrapper para scanner en JoinModal
const handleJoinQRDetect = (detectedCodes: any[]) => {
  const code = onDetect(detectedCodes)
  if (code) {
    console.log('[App] QR detectado en JoinModal:', code)
    joinRoom(code)
  }
}

// Wrapper para scanner en LobbyScreen
const handleGuestQRDetect = (detectedCodes: any[]) => {
  const code = onDetect(detectedCodes)
  if (code) {
    console.log('[App] QR detectado en LobbyScreen:', code)
    // Procesar el código como answer de guest
    handleGuestAnswerProcess(code)
  }
}

const handleGuestAnswerProcess = async (answerCode: string) => {
  try {
    let actualAnswerSDP = answerCode
    let targetSlotId: string | undefined
    
    // Procesar answer para extraer slotId si existe
    try {
      const answerData = JSON.parse(atob(answerCode))
      
      if (answerData.slotId && answerData.sdp) {
        // Es el nuevo formato con slotId incrustado
        actualAnswerSDP = answerData.sdp
        targetSlotId = answerData.slotId
        console.log('[App] Answer con slotId incrustado:', targetSlotId)
      } else {
        // Es formato antiguo (solo SDP)
      }
    } catch (e) {
      // No es JSON válido, asume formato antiguo
    }

    // Si tenemos slotId incrustado, usar ese slot específico
    if (targetSlotId) {
      const slot = guestSlots.value.find(s => s.slotId === targetSlotId)
      
      if (!slot) {
        throw new Error(`Slot ${targetSlotId} no encontrado. Puede que ya haya expirado.`)
      }
      
      if (slot.status !== 'pending') {
        throw new Error(`Slot ${targetSlotId} ya está ${slot.status}.`)
      }
      
      console.log('[App] Aceptando código del jugador para slot:', targetSlotId)
      await acceptGuestAnswer(targetSlotId, actualAnswerSDP)
      
      // Actualizar estado del slot
      slot.status = 'connected'
      
      console.log('[App] Jugador conectado exitosamente al slot:', targetSlotId)
    } else {
      // Formato antiguo: usar el slot pendiente más reciente
      const pendingSlot = guestSlots.value
        .filter(s => s.status === 'pending')
        .sort((a, b) => b.createdAt - a.createdAt)[0]
      
      if (!pendingSlot) {
        throw new Error('No hay ningún slot pendiente. Primero crea un código para un nuevo jugador.')
      }
      
      console.log('[App] Aceptando código del jugador para slot:', pendingSlot.slotId)
      await acceptGuestAnswer(pendingSlot.slotId, actualAnswerSDP)
      
      // Actualizar estado del slot
      pendingSlot.status = 'connected'
      
      console.log('[App] Jugador conectado exitosamente')
    }
    
    console.log('[App] QR procesado exitosamente')
  } catch (e) {
    console.error('[App] Error procesando QR del guest:', e)
    alert('Error procesando el código QR: ' + (e as Error).message)
  }
}

const handleAcceptGuestAnswer = async () => {
  const input = document.getElementById('guest-answer-input') as HTMLInputElement
  if (!input || !input.value.trim()) {
    alert('Pega el código del jugador')
    return
  }
  
  try {
    const answerCode = input.value.trim()
    let actualAnswerSDP = answerCode
    let targetSlotId: string | undefined
    let guestPlayerId: string | undefined
    
    // Procesar answer para extraer slotId y playerId si existen
    try {
      const answerData = JSON.parse(atob(answerCode))
      
      if (answerData.slotId && answerData.sdp) {
        // Es el nuevo formato con slotId incrustado
        actualAnswerSDP = answerData.sdp
        targetSlotId = answerData.slotId
        guestPlayerId = answerData.playerId // Extract guest's player ID
        console.log('[App] Answer con slotId incrustado:', targetSlotId, 'playerId:', guestPlayerId)
      } else {
        // Es formato antiguo (solo SDP)
      }
    } catch (e) {
      // No es JSON válido, asume formato antiguo
    }
    
    // Si tenemos slotId y playerId, crear el mapeo
    if (targetSlotId && guestPlayerId) {
      // Create mapping between slot and player (both directions)
      guestSlotToPlayerId.value.set(targetSlotId, guestPlayerId)
      playerIdToSlot.value.set(guestPlayerId, targetSlotId)
      console.log('[App] Created bidirectional slot mapping:', targetSlotId, '↔', guestPlayerId)
    }
    
    if (targetSlotId) {
      const slot = guestSlots.value.find(s => s.slotId === targetSlotId)
      
      if (!slot) {
        throw new Error(`Slot ${targetSlotId} no encontrado. Puede que ya haya expirado.`)
      }
      
      if (slot.status !== 'pending') {
        throw new Error(`Slot ${targetSlotId} ya está ${slot.status}.`)
      }
      
      console.log('[App] Aceptando código del jugador para slot:', targetSlotId)
      await acceptGuestAnswer(targetSlotId, actualAnswerSDP)
      
      // Actualizar estado del slot
      slot.status = 'connected'
      
      console.log('[App] Jugador conectado exitosamente al slot:', targetSlotId)
    } else {
      // Formato antiguo: usar el slot pendiente más reciente
      const pendingSlot = guestSlots.value
        .filter(s => s.status === 'pending')
        .sort((a, b) => b.createdAt - a.createdAt)[0]
      
      if (!pendingSlot) {
        throw new Error('No hay ningún slot pendiente. Primero crea un código para un nuevo jugador.')
      }
      
      console.log('[App] Aceptando código del jugador para slot:', pendingSlot.slotId)
      await acceptGuestAnswer(pendingSlot.slotId, actualAnswerSDP)
      
      // Actualizar estado del slot
      pendingSlot.status = 'connected'
      
      console.log('[App] Jugador conectado exitosamente')
    }
    
    input.value = ''
  } catch (e) {
    console.error('[App] Error al aceptar jugador:', e)
    alert('Error al aceptar jugador: ' + (e as Error).message)
  }
}

const createGuestOffer = async (): Promise<string> => {
  const creatingId = `creating_${Date.now()}`

  // Añadir slot "creándose" inmediatamente
  creatingSlots.value.push({ id: creatingId, status: 'creating' })

  try {
    const tempSlotId = `guest_slot_${guestSlotCounter.value}`
    guestSlotCounter.value++
    console.log('[App] Creando oferta para guest slot:', tempSlotId)

    const guestOfferCode = await createOfferForGuest(tempSlotId, {
      onMessage: (msg, fromPlayerId) => handleP2PMessage(msg, fromPlayerId),
      onConnected: (playerId) => {
        console.log('[App] Guest conectado:', playerId)
        // Initialize pong time when player is actually connected
        // If it's a guest_slot, also initialize for the real player ID
        lastPongTimes.value.set(playerId, Date.now())

        // If this is a guest_slot, also track the real player ID
        const realPlayerId = guestSlotToPlayerId.value.get(playerId)
        if (realPlayerId) {
          lastPongTimes.value.set(realPlayerId, Date.now())
          console.log('[App] Initialized ping tracking for both slot and player:', playerId, '↔', realPlayerId)
        }

        // Actualizar el slot a conectado
        const slot = guestSlots.value.find(s => s.slotId === tempSlotId)
        if (slot) {
          slot.status = 'connected'
        }
      },
      onDisconnected: (playerId) => {
        console.log('[App] Guest desconectado:', playerId)
        handlePlayerDisconnection(playerId)
      }
    })

    // Crear el nuevo slot
    const newSlot: GuestSlot = {
      id: `slot_${Date.now()}`,
      slotId: tempSlotId,
      offerCode: guestOfferCode,
      status: 'pending',
      createdAt: Date.now()
    }

    guestSlots.value.push(newSlot)

    // In internet mode, store the offer on the server
    if (configStore.options.connectionMode === 'internet' && currentRoomId.value) {
      console.log('[App] Storing guest slot on server:', tempSlotId)
      const success = await addSlotToServer(currentRoomId.value, tempSlotId, guestOfferCode)
      if (!success) {
        console.error('[App] Failed to store slot on server')
        // Don't throw - the slot can still work in local mode
      }
    }

    // Eliminar el slot "creándose"
    creatingSlots.value = creatingSlots.value.filter(s => s.id !== creatingId)

    console.log('[App] Slot creado:', newSlot.id)

    return guestOfferCode
  } catch (e) {
    // Eliminar el slot "creándose" si hay error
    creatingSlots.value = creatingSlots.value.filter(s => s.id !== creatingId)
    console.error('[App] Error al crear oferta:', e)
    throw e
  }
}

// Función para copiar el código de un slot específico
const copySlotCode = async (slotId: string): Promise<void> => {
  const slot = guestSlots.value.find(s => s.id === slotId)
  if (slot) {
    await navigator.clipboard.writeText(slot.offerCode)
    console.log('[App] Código copiado para slot:', slotId)
  }
}

// Función para eliminar un slot pendiente
const deleteGuestSlot = (slotId: string): void => {
  console.log('[App] deleteGuestSlot - deleting slot:', slotId)
  
  const slotIndex = guestSlots.value.findIndex(s => s.id === slotId)
  if (slotIndex === -1) {
    console.warn('[App] deleteGuestSlot - slot not found:', slotId)
    return
  }
  
  const slot = guestSlots.value[slotIndex]
  
  // Cerrar la conexión WebRTC asociada si existe
  if (slot.slotId) {
    closeGuestConnection(slot.slotId)
  }
  
  // Eliminar el slot del array
  guestSlots.value.splice(slotIndex, 1)
  
  console.log('[App] deleteGuestSlot - slot deleted:', slotId, 'remaining slots:', guestSlots.value.length)
}

// Gestures
useGestures({
  isEnabled: computed(() => isMyTurn.value && gameStarted.value),
  onDoubleTap: () => {
    const wordEl = document.getElementById('word-display')
    if (wordEl) {
      wordEl.classList.add('animate-pulse-once')
      setTimeout(() => wordEl.classList.remove('animate-pulse-once'), 300)
    }
    gameCorrectAnswer()
  },
  onShake: () => {
    if (isMyTurn.value) {
      gameSkipWord()
    }
  }
})

// P2P Message Handler - maneja mensajes multi-jugador
const handleP2PMessage = (message: P2PMessage, fromPlayerId?: string): void => {
  console.log('[App] handleP2PMessage - type:', message.type, 'from:', fromPlayerId)

  switch (message.type) {
    case 'assign-name':
      // El host nos asigna nuestro nombre definitivo
      playerStore.setMyPlayerName(message.name)
      // Actualizar también en la lista de jugadores
      const me = playerStore.players.find(p => p.id === playerStore.myPlayerId)
      if (me) {
        me.name = message.name
      }
      console.log('[App] Received assigned name from host:', message.name)
      break
    case 'player-joined':
      if (!playerStore.players.find(p => p.id === message.player.id)) {
        playerStore.addPlayer(message.player)
        
        // Si soy guest y recibo player-joined del host, navegar a lobby
        if (!playerStore.isHost && message.player.id !== playerStore.myPlayerId) {
          console.log('[App] Guest received confirmation from host, navigating to lobby')
          uiStore.navigateTo('lobby')
        }
      }
      // Broadcast player-joined a todos los demás (solo host)
      if (playerStore.isHost && fromPlayerId) {
        broadcast(message, fromPlayerId)
      }
      break
    case 'player-disconnected':
      // Host notified us that a player disconnected
      if (!playerStore.isHost) {
        console.log('[App] Player disconnected notification:', message.playerId)
        const disconnectedPlayer = playerStore.players.find(p => p.id === message.playerId)
        
        // Check if it was the host who disconnected
        if (disconnectedPlayer?.isHost) {
          console.log('[App] Host disconnected - navigating to host-disconnected screen')
          uiStore.navigateTo('host-disconnected')
        } else if (gameStarted.value) {
          // If a player disconnected during game, show player-disconnected screen
          console.log('[App] Player disconnected during game - navigating to player-disconnected screen')
          uiStore.navigateTo('player-disconnected')
        } else {
          // Just remove the player in lobby
          console.log('[App] Removing player from lobby (guest side):', disconnectedPlayer?.name || message.playerId)
          playerStore.removePlayer(message.playerId)
        }
        lastPongTimes.value.delete(message.playerId)
      } else {
        // Host broadcasts the disconnection to other guests
        if (fromPlayerId) {
          broadcast(message, fromPlayerId)
        }
      }
      break
    case 'join-game':
      if (playerStore.isHost) {
        const newPlayer = message.player

        // NOTA: Ya no necesitamos remapear peer connections
        // Las conexiones se mantienen con guest_slot_X y usamos el mapping guestSlotToPlayerId
        // para relacionar guest_slot_X con el playerId real del guest
        console.log('[App] Using slot-based connection system for player:', newPlayer.id, 'from slot:', fromPlayerId)

        // Generar nombre único para el nuevo jugador (host conoce todos los nombres)
        const usedNames = new Set(playerStore.players.map(p => p.name))
        const uniqueName = generatePlayerName(usedNames)
        console.log('[App] Assigned unique name to guest:', uniqueName)

        // Crear jugador con nombre único asignado por el host
        const playerWithUniqueName = {
          ...newPlayer,
          name: uniqueName,
          connectionStatus: 'connected' as const
        }

        if (!playerStore.players.find(p => p.id === newPlayer.id)) {
          playerStore.addPlayer(playerWithUniqueName)
        }

        // Enviar nombre asignado al guest
        sendTo(resolvePlayerIdToSlot(newPlayer.id), {
          type: 'assign-name',
          name: uniqueName
        } as P2PMessage)

        // Enviar lista completa de jugadores existentes al nuevo guest
        console.log('[App] Sending existing players list to new player:', newPlayer.id)
        playerStore.players.forEach(existingPlayer => {
          if (existingPlayer.id !== newPlayer.id) {
            console.log('[App] Sending player-joined for existing player:', existingPlayer.name)
            sendTo(resolvePlayerIdToSlot(newPlayer.id), {
              type: 'player-joined',
              player: existingPlayer
            } as P2PMessage)
          }
        })

        // Broadcast del nuevo jugador a todos los demás con el nombre único
        broadcast({
          type: 'player-joined',
          player: playerWithUniqueName
        } as P2PMessage, newPlayer.id)
      }
      break
    case 'correct-answer':
    case 'skip-word':
      // Reenviar estos mensajes a todos los jugadores (solo host)
      if (playerStore.isHost && fromPlayerId) {
        broadcast(message, fromPlayerId)
      }
      // También procesar localmente para el game composable
      gameHandleP2PMessage(message)
      break
    case 'ping':
      // Guest received ping from host, respond with pong
      if (!playerStore.isHost) {
        console.log('[App] Received ping from host, sending pong')
        send({ type: 'pong', originalTimestamp: message.timestamp } as P2PMessage)
      }
      break
    case 'pong':
      // Host received pong from guest
      if (playerStore.isHost && fromPlayerId) {
        console.log('[App] Received pong from:', fromPlayerId)
        const now = Date.now()
        
        // Calculate latency
        const latency = now - message.originalTimestamp
        console.log('[App] Latency for', fromPlayerId, ':', latency, 'ms')
        
        // Update lastPongTime for the ID that sent the pong
        lastPongTimes.value.set(fromPlayerId, now)
        
        // If it's a guest_slot, also update the corresponding player ID using the mapping
        let realPlayerId: string | undefined
        if (fromPlayerId.startsWith('guest_slot_')) {
          realPlayerId = guestSlotToPlayerId.value.get(fromPlayerId)
          if (realPlayerId) {
            lastPongTimes.value.set(realPlayerId, now)
            console.log('[App] Also updated lastPongTime for player:', realPlayerId)
          }
        } else {
          // If it's a real player ID, also update the corresponding slot
          const slotId = playerIdToSlot.value.get(fromPlayerId)
          if (slotId) {
            lastPongTimes.value.set(slotId, now)
            console.log('[App] Also updated lastPongTime for slot:', slotId)
          }
        }
        
        // Update player latency in store
        playerStore.updatePlayerLatency(realPlayerId || fromPlayerId, latency)
        
        // Update connection status if was disconnected
        playerStore.updateConnectionStatus(fromPlayerId, 'connected')
      }
      break
    // Los otros mensajes se manejan en game composable
    default:
      gameHandleP2PMessage(message)
      break
  }
}

const createRoom = async (): Promise<void> => {
  if (!peerInitialized.value) {
    alert('Espera a que se inicialice la conexión P2P...')
    return
  }

  creatingRoom.value = true

  try {
    playerStore.setAsHost()

    // Generar room ID separado del player ID
    const roomId = generateRoomId()
    playerStore.setRoomId(roomId)
    currentRoomId.value = roomId

    playerStore.setMyPlayerId(generatePlayerId())

    // Crear Set con nombres ya usados para evitar duplicados
    const usedNames = new Set(playerStore.players.map(p => p.name))
    const hostName = generatePlayerName(usedNames)
    playerStore.setMyPlayerName(hostName)
    playerStore.addPlayer({
      id: playerStore.myPlayerId!,
      name: hostName,
      score: 0,
      isHost: true,
      connectionStatus: 'connected'
    })

    // Reset guest slot counter
    guestSlotCounter.value = 0

    const offer = await createOffer()
    playerStore.setOfferCode(offer)

    // Store offer on server if in internet mode
    if (configStore.options.connectionMode === 'internet') {
      console.log('[App] Creating room on server with internet mode')
      const success = await createRoomWithServer(roomId, 'guest_slot_0', offer, 'internet')
      if (!success) {
        alert('Error al crear la sala en el servidor. Intenta con modo local.')
        return
      }

      // Start polling for guest answers
      startPollingForAnswers(roomId, async (slotId, answer, playerId) => {
        console.log('[App] Received guest answer from server:', slotId, playerId)

        // Create bidirectional mapping
        guestSlotToPlayerId.value.set(slotId, playerId)
        playerIdToSlot.value.set(playerId, slotId)

        // Accept the guest answer
        try {
          // Parse answer to extract SDP if it's the new format
          let actualAnswerSDP = answer
          try {
            const answerData = JSON.parse(atob(answer))
            if (answerData.sdp) {
              actualAnswerSDP = answerData.sdp
            }
          } catch (e) {
            // Not JSON, use raw answer
          }

          await acceptGuestAnswer(slotId, actualAnswerSDP)

          // Find and update the slot status
          const slot = guestSlots.value.find(s => s.slotId === slotId)
          if (slot) {
            slot.status = 'connected'
          }

          console.log('[App] Guest connected via server signaling:', playerId)
        } catch (e) {
          console.error('[App] Error accepting guest answer from server:', e)
        }
      })
    }

    uiStore.navigateTo('lobby')

    setTimeout(() => {
      generateQR(offer, 'qr-code')
    }, 100)
  } finally {
    creatingRoom.value = false
  }
}

const copyCode = async (): Promise<void> => {
  try {
    await navigator.clipboard.writeText(playerStore.offerCode)
    alert('¡Código copiado!')
  } catch {
    const textarea = document.createElement('textarea')
    textarea.value = playerStore.offerCode
    document.body.appendChild(textarea)
    textarea.select()
    document.execCommand('copy')
    document.body.removeChild(textarea)
  }
}

const joinRoom = async (joinCode: string): Promise<void> => {
  try {
    playerStore.setAsGuest()
    playerStore.setMyPlayerId(generatePlayerId())

    // Usar nombre temporal, el host asignará el nombre definitivo
    const tempName = `Guest_${Math.random().toString(36).substr(2, 4)}`
    playerStore.setMyPlayerName(tempName)

    // Crear answer usando el offer del host
    const answer = await createAnswer(joinCode.trim(), handleP2PMessage, handleHostDisconnection)

    if (!answer || answer.length < 100) {
      throw new Error('Código de respuesta inválido')
    }

    playerStore.setOfferCode(answer)
    uiStore.closeJoinModal()

    // En internet mode, send answer to server instead of showing QR
    if (configStore.options.connectionMode === 'internet') {
      // Extract roomId and slotId from offer code
      let roomId: string | undefined
      let slotId: string | undefined

      try {
        const offerData = JSON.parse(atob(joinCode.trim()))
        if (offerData.roomId && offerData.slotId) {
          roomId = offerData.roomId
          slotId = offerData.slotId
          console.log('[App] Parsed offer metadata:', { roomId, slotId })
        }
      } catch (e) {
        // Not the new format, fall back to local mode behavior
        console.warn('[App] Could not parse offer metadata, falling back to local mode')
      }

      if (roomId && slotId) {
        console.log('[App] Joining room via server:', roomId, 'slot:', slotId)
        const success = await joinRoomViaServer(roomId, slotId, answer, playerStore.myPlayerId!)

        if (!success) {
          alert('Error al unirse a la sala. El QR puede haber expirado.')
          // CLEANUP: Reset state on error
          playerStore.setMyPlayerName('')
          uiStore.navigateTo('home')
          return
        }

        // Add player AFTER successful server connection
        playerStore.addPlayer({
          id: playerStore.myPlayerId!,
          name: tempName,
          score: 0,
          isHost: false,
          connectionStatus: 'pending' as const
        })

        console.log('[App] Answer sent to server, waiting for host to accept')
        // In internet mode, we don't show QR - just wait for host
        // The QR generation is skipped in GuestWaitingScreen for internet mode
        uiStore.navigateTo('guest-waiting')
      } else {
        // Fallback: show QR if we couldn't parse the metadata (add player before showing QR)
        playerStore.addPlayer({
          id: playerStore.myPlayerId!,
          name: tempName,
          score: 0,
          isHost: false,
          connectionStatus: 'pending' as const
        })
        console.warn('[App] No roomId/slotId in offer, showing QR (local mode fallback)')
        setTimeout(() => {
          generateQR(answer, 'guest-qr-code')
        }, 100)
        uiStore.navigateTo('guest-waiting')
      }
    } else {
      // Local mode: add player before showing QR
      playerStore.addPlayer({
        id: playerStore.myPlayerId!,
        name: tempName,
        score: 0,
        isHost: false,
        connectionStatus: 'pending' as const
      })
      setTimeout(() => {
        generateQR(answer, 'guest-qr-code')
      }, 100)
      uiStore.navigateTo('guest-waiting')
    }

    // Guardar answer en variable global para testing
    ;(window as any).lastGuestAnswer = answer

    // Enviar mensaje join-game cuando el data channel abra
    // Usamos un timeout para dar tiempo a que la conexión se establezca
    setTimeout(() => {
      try {
        send({
          type: 'join-game',
          player: {
            id: playerStore.myPlayerId!,
            name: playerStore.myPlayerName,
            score: 0,
            isHost: false
          }
        } as P2PMessage)
        console.log('[App] Sent join-game message')
        // NO navegar automáticamente - esperar confirmación del host
        // La navegación a 'lobby' ocurrirá cuando se reciba 'player-joined'
      } catch (e) {
        console.error('[App] Error sending join-game:', e)
      }
    }, 1000) // Esperar 1 segundo para que el data channel abra
  } catch (e) {
    alert('Código inválido: ' + (e as Error).message)
    // CLEANUP: Ensure clean state on any error
    playerStore.setMyPlayerName('')
  }
}


// Función separada para aceptar el answer del guest (después de crear la oferta)
const acceptGuestResponse = async (guestSlotId: string, answerCode: string): Promise<void> => {
  try {
    await acceptGuestAnswer(guestSlotId, answerCode)
    alert('¡Guest conectado!')
  } catch (e) {
    alert('Error al conectar guest: ' + (e as Error).message)
  }
}

const startGame = (): void => {
  // Stop polling when game starts (for internet mode)
  if (currentRoomId.value) {
    stopPolling(currentRoomId.value)
    console.log('[App] Stopped polling for room:', currentRoomId.value)
  }

  if (playerStore.isHost && configStore.options.categoryMode === 'roulette') {
    showRouletteModal.value = true
  } else {
    gameStartGame()
  }
}

const onRouletteSelect = (category: string): void => {
  configStore.updateOptions({ 
    categoryMode: 'select',
    selectedCategories: [category]
  })
  showRouletteModal.value = false
  gameStartGame()
}

const playAgain = (): void => {
  location.reload()
}

const goHome = (): void => {
  // Reload the page to reset all state
  location.reload()
}

// Handle host disconnection from guest perspective
const handleHostDisconnection = (): void => {
  console.log('[App] Host disconnected - navigating to host-disconnected screen')
  uiStore.navigateTo('host-disconnected')
}

// Handle complete player disconnection
const handlePlayerDisconnection = (playerIdOrSlot: string): void => {
  // Prevent duplicate disconnections
  if (disconnectingPlayers.value.has(playerIdOrSlot)) {
    console.log('[App] Already disconnecting player:', playerIdOrSlot)
    return
  }
  
  disconnectingPlayers.value.add(playerIdOrSlot)
  console.log('[App] Handling disconnection for:', playerIdOrSlot)
  
  // Determine if we received a slot ID or player ID, and get both if possible
  let realPlayerId = playerIdOrSlot
  let slotId = playerIdOrSlot
  
  // If it's a slot ID, find the corresponding player ID
  if (playerIdOrSlot.startsWith('guest_slot_')) {
    slotId = playerIdOrSlot
    realPlayerId = guestSlotToPlayerId.value.get(playerIdOrSlot) || playerIdOrSlot
    console.log('[App] Slot ID received, resolved to player:', slotId, '→', realPlayerId)
  } 
  // If it's a player ID, find the corresponding slot ID
  else {
    const foundSlot = playerIdToSlot.value.get(playerIdOrSlot)
    if (foundSlot) {
      slotId = foundSlot
      console.log('[App] Player ID received, resolved to slot:', realPlayerId, '→', slotId)
    }
  }
  
  // Check if it's the host that disconnected
  const player = playerStore.players.find(p => p.id === realPlayerId)
  const playerName = player?.name || 'Unknown'
  const isHostDisconnection = player?.isHost || false
  
  console.log('[App] Disconnection info - player:', playerName, 'isHost:', isHostDisconnection, 'gameStarted:', gameStarted.value)
  
  if (isHostDisconnection) {
    console.log('[App] Host disconnected - navigating to host-disconnected screen')
    uiStore.navigateTo('host-disconnected')
    
    // Clean up everything for the host
    playerStore.removePlayer(realPlayerId)
    lastPongTimes.value.delete(realPlayerId)
    lastPongTimes.value.delete(slotId)
    
    disconnectingPlayers.value.delete(playerIdOrSlot)
    return
  }
  
  // If a player disconnected during game, show player-disconnected screen
  if (gameStarted.value) {
    console.log('[App] Player disconnected during game - navigating to player-disconnected screen')
    uiStore.navigateTo('player-disconnected')
    
    // Clean up the disconnected player
    playerStore.removePlayer(realPlayerId)
    lastPongTimes.value.delete(realPlayerId)
    lastPongTimes.value.delete(slotId)
    
    disconnectingPlayers.value.delete(playerIdOrSlot)
    return
  }
  
  // Otherwise, just remove the player (lobby scenario)
  console.log('[App] Removing player from store (lobby):', playerName)
  playerStore.removePlayer(realPlayerId)
  
  // Remove guest slot if exists
  const slotIndex = guestSlots.value.findIndex(s => s.slotId === slotId)
  if (slotIndex !== -1) {
    console.log('[App] Removing guest slot:', slotId)
    guestSlots.value.splice(slotIndex, 1)
  }
  
  // Clean up ping tracking for both IDs
  lastPongTimes.value.delete(realPlayerId)
  lastPongTimes.value.delete(slotId)
  
  // Clean up slot mapping (both directions)
  guestSlotToPlayerId.value.delete(slotId)
  playerIdToSlot.value.delete(realPlayerId)
  console.log('[App] Cleaned up bidirectional slot mapping:', slotId, '↔', realPlayerId)
  
  // Close peer connection
  closeGuestConnection(slotId)
  
  // Notify other players (only if there are still guests connected)
  if (hasConnectedGuests.value) {
    broadcast({
      type: 'player-disconnected',
      playerId: realPlayerId
    } as P2PMessage)
  }
  
  disconnectingPlayers.value.delete(playerIdOrSlot)
  console.log('[App] Player disconnection complete:', realPlayerId, '(', slotId, ')')
}

// Ping-Pong System for connectivity tracking
const lastPongTimes = ref<Map<string, number>>(new Map())
const pingInterval = ref<number | null>(null)
const disconnectingPlayers = ref<Set<string>>(new Set()) // Prevent duplicate disconnections
const guestSlotToPlayerId = ref<Map<string, string>>(new Map()) // Track guest_slot -> player_id mapping
const playerIdToSlot = ref<Map<string, string>>(new Map()) // Track player_id -> guest_slot mapping (reverse lookup)

const startPingSystem = (): void => {
  if (pingInterval.value) return
  
  pingInterval.value = window.setInterval(() => {
    if (!playerStore.isHost || !hasConnectedGuests.value) {
      stopPingSystem()
      return
    }

    const now = Date.now()
    const gracePeriod = 30000 // 30 seconds grace period for new players
    const timeout = 5000 // 5 seconds timeout

    playerStore.players.forEach(player => {
      if (player.isHost) return // Don't ping ourselves
      if (disconnectingPlayers.value.has(player.id)) return // Already disconnecting
      
      // Skip if player's connection status is already marked as disconnected
      if (player.connectionStatus === 'disconnected') {
        console.log('[App] Skipping ping for disconnected player:', player.name)
        return
      }

      const lastPong = lastPongTimes.value.get(player.id)
      
      if (lastPong === undefined) {
        // First time seeing this player - initialize but don't ping yet
        console.log('[App] Initializing ping tracking for:', player.name)
        lastPongTimes.value.set(player.id, now)
      } else {
        const timeSinceLastPong = now - lastPong
        const timeSinceFirstSeen = now - lastPong
        
        // Check if player has a valid slot mapping before attempting to ping
        const targetSlot = resolvePlayerIdToSlot(player.id)
        const hasValidConnection = peerConnections.value.has(targetSlot)
        
        if (!hasValidConnection) {
          console.warn('[App] Player', player.name, 'has no valid peer connection (slot:', targetSlot, ')')
          // If the player has no valid connection but is still marked as connected, mark them as disconnected
          if (player.connectionStatus === 'connected') {
            console.log('[App] Marking player as disconnected due to missing connection:', player.name)
            playerStore.updateConnectionStatus(player.id, 'disconnected')
          }
          return
        }
        
        // Give grace period to new players
        if (timeSinceFirstSeen < gracePeriod) {
          console.log('[App] Grace period for', player.name, '- sending ping')
          sendTo(targetSlot, { type: 'ping', timestamp: now } as P2PMessage)
        } 
        // After grace period, check for timeout
        else if (timeSinceLastPong > timeout && player.connectionStatus === 'connected') {
          console.log('[App] Player timeout:', player.name, `(${timeSinceLastPong}ms)`)
          handlePlayerDisconnection(player.id)
        } 
        // Player is alive, send next ping
        else {
          sendTo(targetSlot, { type: 'ping', timestamp: now } as P2PMessage)
        }
      }
    })
  }, 10000) // Every 10 seconds

  console.log('[App] Ping system started')
}

const stopPingSystem = (): void => {
  if (pingInterval.value) {
    clearInterval(pingInterval.value)
    pingInterval.value = null
    console.log('[App] Ping system stopped')
  }
}

// Watch for connected guests to manage ping system
const hasConnectedGuests = computed(() => {
  return playerStore.isHost && playerStore.players.filter(p => !p.isHost).length > 0
})

watch(hasConnectedGuests, (hasGuests) => {
  if (hasGuests) {
    startPingSystem()
  } else {
    stopPingSystem()
  }
}, { immediate: false })

onMounted(async () => {
  // Expose function for testing (accept guest answer from clipboard)
  ;(window as any).acceptGuestFromClipboard = async (slotId = 'guest_slot_0') => {
    const answerCode = await navigator.clipboard.readText()
    await acceptGuestResponse(slotId, answerCode)
  }
  // Also expose a specific function for guest_slot_1
  ;(window as any).acceptGuest1 = async () => {
    const answerCode = await navigator.clipboard.readText()
    await acceptGuestResponse('guest_slot_1', answerCode)
  }

  await configStore.loadCategories()
  await initializePeer(
    () => {
      if (playerStore.isHost) {
        uiStore.navigateTo('lobby')
      } else {
        send({
          type: 'join-game',
          player: {
            id: playerStore.myPlayerId!,
            name: playerStore.myPlayerName,
            score: 0,
            isHost: false
          }
        } as P2PMessage)
        uiStore.navigateTo('lobby')
      }
    },
    handleP2PMessage,
    () => {
      // Handle disconnection
      if (!playerStore.isHost) {
        // Guest disconnected from host
        console.log('[App] Guest detected disconnection from host')
        handleHostDisconnection()
      } else {
        // Host disconnected (shouldn't happen normally)
        console.log('[App] Host disconnected - navigating to home')
        uiStore.navigateTo('home')
      }
    }
  )
})

// Cleanup on component unmount
onUnmounted(() => {
  // Stop all polling intervals when component unmounts
  stopAllPolling()
  console.log('[App] Cleanup: stopped all polling on unmount')
})
</script>

<template>
  <div class="screen">
    <component
      :is="{
        home: HomeScreen,
        lobby: LobbyScreen,
        game: GameScreen,
        results: ResultsScreen,
        'guest-waiting': GuestWaitingScreen,
        'host-disconnected': HostDisconnectedScreen,
        'player-disconnected': PlayerDisconnectedScreen
      }[uiStore.currentScreen]"
      @open-join-modal="uiStore.openJoinModal"
      @open-options-modal="uiStore.openOptionsModal"
      @open-room-settings-modal="uiStore.openRoomSettingsModal"
      @go-home="goHome"
      :category-mode="configStore.options.categoryMode"
      :difficulty="configStore.options.difficulty"
      :rounds-per-player="configStore.options.roundsPerPlayer"
      :timer-duration="configStore.options.timerDuration"
      @open-qr-modal="(code: string) => uiStore.openQRModal(code)"
      v-bind="{
        players: playerStore.players,
        offerCode: playerStore.offerCode,
        currentRound,
        totalRounds,
        currentPlayer,
        isMyTurn,
        timeLeft,
        currentCategory,
        currentWord,
        rankings,
        options: configStore.options,
        creatingRoom,
        createRoom,
        copyCode,
        startGame,
        playAgain,
        joinRoom,
        createGuestOffer,
        acceptGuestAnswer: handleAcceptGuestAnswer,
        onRouletteSelect,
        showRouletteModal,
        categories: configStore.categories,
        startGuestScanner: () => {},  // No longer needed with QrcodeStream component
        QrcodeStream: QrcodeStream,    // Pass the component
        onGuestQRDetect: handleGuestQRDetect,
        onCameraReady: onCameraReady,
        onScannerError: onError,
        selectedCameraId: selectedCameraId,
        generateQR,
        myPlayerId: playerStore.myPlayerId,
        guestSlots,
        creatingSlots,
        copySlotCode,
        deleteGuestSlot,
        connectionMode: configStore.options.connectionMode
      }"
    />

    <JoinModal
      v-model:show="uiStore.showJoinModal"
      :QrcodeStream="QrcodeStream"
      :onQRDetect="handleJoinQRDetect"
      :onCameraReady="onCameraReady"
      :onScannerError="onError"
      :selectedCameraId="selectedCameraId"
      @join="joinRoom"
    />

    <OptionsModal
      v-model:show="uiStore.showOptionsModal"
      :cameras="availableCameras"
      :selectedCamera="selectedCameraId"
      @camera-change="(id) => qrCode.saveCameraId(id)"
    />

    <RoomSettingsModal
      v-model:show="uiStore.showRoomSettingsModal"
      :options="configStore.options"
      :categories="configStore.categories"
      @update:options="(newOptions) => configStore.updateOptions(newOptions)"
    />

    <RouletteModal
      v-model:show="showRouletteModal"
      :categories="configStore.categories"
      :selectedCategories="configStore.options.selectedCategories"
      @select="onRouletteSelect"
    />

    <QRModal
      v-model:show="uiStore.showQRModal"
      :code="uiStore.currentQRCode"
    />
  </div>
</template>
